import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { PinGate } from "../components/admin/PinGate";
import { Kpis } from "../components/admin/Kpis";
import { TestMap } from "../components/admin/TestMap";
import { IssuesList } from "../components/admin/IssuesList";
import { RepStats } from "../components/admin/RepStats";
import { TestLog } from "../components/admin/TestLog";
import { ThresholdsDialog } from "../components/admin/ThresholdsDialog";
import { DateRange } from "../components/admin/DateRange";
import { Collapsible } from "../components/admin/Collapsible";
import { ClearDataDialog } from "../components/admin/ClearDataDialog";
import { RewardsAdmin } from "../components/admin/RewardsAdmin";
import { NetFilter, applyNetFilter } from "../components/NetFilter";
import { Reports } from "../components/reports/Reports";

const REFRESH_MS = 5 * 60 * 1000;
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 10); };

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("admin_pin"));
  const [tests, setTests] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [team, setTeam] = useState("");
  const [net, setNet] = useState({ isp: "Telkom", net: "" });
  const [from, setFrom] = useState(todayIso);
  const [to, setTo] = useState(todayIso);
  const [view, setView] = useState("overview");
  const [updated, setUpdated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([api.tests(), api.settings()]);
      setTests(t); setThresholds(s); setUpdated(new Date());
    } catch (e) {
      if (e.response?.status === 401) { sessionStorage.removeItem("admin_pin"); setAuthed(false); }
      else toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [authed, load]);

  const teams = useMemo(() => [...new Set(tests.map((t) => t.team))].sort(), [tests]);
  const filtered = useMemo(() => applyNetFilter(tests, net).filter((t) => {
    const c = new Date(t.created_at);
    const d = new Date(c.getTime() - c.getTimezoneOffset() * 6e4).toISOString().slice(0, 10);
    return (!team || t.team === team) && (!from || d >= from) && (!to || d <= to);
  }), [tests, team, from, to, net]);
  const undated = useMemo(() => applyNetFilter(tests, net).filter((t) => !team || t.team === team), [tests, team, net]);
  const rangeLabel = from || to ? `${from || "start"} → ${to || "today"}` : "All time";

  if (!authed) return <PinGate onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen relative z-10">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="back-to-test-link" className="text-neutral-400 hover:text-white no-print"><ArrowLeft className="w-4 h-4" /></Link>
          <img src="/logo-64.png" alt="CellCheckPoint" className="w-10 h-10" data-testid="admin-logo" />
          <h1 className="font-display text-4xl sm:text-5xl tracking-wide">Network <span className="text-[#007AFF]">Command</span></h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 no-print">
          <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
          <select data-testid="team-filter" value={team} onChange={(e) => setTeam(e.target.value)} className="field py-2">
            <option value="">All provinces</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <NetFilter tests={tests} value={net} onChange={setNet} />
          {thresholds && <ThresholdsDialog value={thresholds} onSaved={() => load()} />}
          <ClearDataDialog count={tests.length} onCleared={load} />
          {updated && <span className="text-[10px] text-neutral-500 whitespace-nowrap" data-testid="last-updated" title="Auto-refreshes every 5 minutes">Updated {updated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · auto 5 min</span>}
          <button data-testid="refresh-button" onClick={load} className="btn-ghost"><RefreshCw className="w-4 h-4" /></button>
          <button data-testid="logout-button" onClick={() => { sessionStorage.removeItem("admin_pin"); setAuthed(false); }} className="btn-ghost"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>
      <nav className="flex border-b border-white/10 no-print" data-testid="admin-tabs">
        {[["overview", "Live Dashboard"], ["reports", "Reports"], ["rewards", "Rewards"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setView(k)}
            className={`px-6 py-3 label border-r border-white/10 ${view === k ? "text-white bg-[#121212] shadow-[inset_0_-2px_0_#007AFF]" : "hover:text-white"}`}>{l}</button>
        ))}
      </nav>
      {view === "rewards" ? <RewardsAdmin /> : view === "reports" ? (
        <Reports tests={filtered} allTests={undated} rangeLabel={rangeLabel} teamLabel={[team || "All provinces", net.isp, net.net].filter(Boolean).join(" · ")} />
      ) : (
        <>
          <Collapsible id="kpis" title="Headline numbers"><Kpis tests={filtered} undated={undated} /></Collapsible>
          <Collapsible id="map" title="Map & issues">
          <div className="grid grid-cols-1 lg:grid-cols-4 border-b border-white/10">
            <div className="lg:col-span-3 border-r border-white/10 h-[460px]"><TestMap tests={filtered} /></div>
            <IssuesList tests={filtered} />
          </div>
          </Collapsible>
          <Collapsible id="log" title="Full test log"><TestLog tests={filtered} onDelete={async (id) => { await api.deleteTest(id); load(); }} /></Collapsible>
          <Collapsible id="reps" title="Rep stats"><RepStats tests={filtered} /></Collapsible>
        </>
      )}
    </div>
  );
}
